import type { Rng } from '../core/rng';
import { C } from './palette';
import type { Renderer } from './renderer';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  gravity: number;
  color: string;
  /** 'square' | 'spark' | 'shard' */
  kind: number;
  active: boolean;
}

interface Floater {
  x: number;
  y: number;
  vy: number;
  life: number;
  text: string;
  color: string;
  active: boolean;
}

const MAX_PARTICLES = 320;
const MAX_FLOATERS = 24;

/** Particles and floating score numbers. Pooled: no allocation during play. */
export class ParticleSystem {
  private pool: Particle[] = [];
  private floaters: Floater[] = [];

  constructor(private rng: Rng) {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.pool.push({
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        life: 0,
        max: 1,
        size: 1,
        gravity: 0,
        color: '#fff',
        kind: 0,
        active: false,
      });
    }
    for (let i = 0; i < MAX_FLOATERS; i++) {
      this.floaters.push({ x: 0, y: 0, vy: 0, life: 0, text: '', color: '#fff', active: false });
    }
  }

  clear(): void {
    for (const p of this.pool) p.active = false;
    for (const f of this.floaters) f.active = false;
  }

  private take(): Particle | null {
    for (const p of this.pool) if (!p.active) return p;
    return null;
  }

  emit(
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    color: string,
    size = 2,
    gravity = 300,
    kind = 0,
  ): void {
    const p = this.take();
    if (!p) return;
    p.x = x;
    p.y = y;
    p.vx = vx;
    p.vy = vy;
    p.life = life;
    p.max = life;
    p.color = color;
    p.size = size;
    p.gravity = gravity;
    p.kind = kind;
    p.active = true;
  }

  /** Floating score / bonus text. */
  float(x: number, y: number, text: string, color: string = C.white): void {
    let f = this.floaters.find((v) => !v.active);
    if (!f) {
      f = this.floaters[0];
    }
    f.x = x;
    f.y = y;
    f.vy = -26;
    f.life = 1.0;
    f.text = text;
    f.color = color;
    f.active = true;
  }

  /* ---------------- canned effects ---------------- */

  coinSparkle(x: number, y: number): void {
    for (let i = 0; i < 6; i++) {
      this.emit(
        x + this.rng.range(-4, 4),
        y + this.rng.range(-4, 4),
        this.rng.range(-40, 40),
        this.rng.range(-90, -30),
        this.rng.range(0.25, 0.5),
        i % 2 ? C.coinLight : C.coin,
        2,
        260,
        1,
      );
    }
  }

  brickShards(x: number, y: number, color: string = C.brick, dark: string = C.brickDark): void {
    for (let i = 0; i < 8; i++) {
      const left = i % 2 === 0;
      this.emit(
        x + (left ? 3 : 11),
        y + (i < 4 ? 3 : 11),
        (left ? -1 : 1) * this.rng.range(30, 90),
        this.rng.range(-200, -90),
        this.rng.range(0.5, 0.9),
        i % 3 === 0 ? dark : color,
        3,
        520,
        2,
      );
    }
  }

  puff(x: number, y: number, color: string = C.white, count = 6): void {
    for (let i = 0; i < count; i++) {
      this.emit(
        x + this.rng.range(-5, 5),
        y + this.rng.range(-3, 3),
        this.rng.range(-50, 50),
        this.rng.range(-60, -10),
        this.rng.range(0.2, 0.45),
        color,
        2,
        60,
        0,
      );
    }
  }

  dust(x: number, y: number, dir: number): void {
    this.emit(
      x,
      y,
      -dir * this.rng.range(20, 60),
      this.rng.range(-40, -8),
      this.rng.range(0.15, 0.35),
      C.mortar,
      2,
      120,
      0,
    );
  }

  defeat(x: number, y: number, color: string): void {
    for (let i = 0; i < 10; i++) {
      this.emit(
        x,
        y,
        this.rng.range(-110, 110),
        this.rng.range(-170, -40),
        this.rng.range(0.35, 0.7),
        i % 3 === 0 ? C.white : color,
        2,
        420,
        1,
      );
    }
  }

  firework(x: number, y: number, color: string): void {
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      const sp = this.rng.range(50, 130);
      this.emit(
        x,
        y,
        Math.cos(a) * sp,
        Math.sin(a) * sp,
        this.rng.range(0.4, 0.9),
        i % 4 === 0 ? C.white : color,
        2,
        120,
        1,
      );
    }
  }

  update(dt: number): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    for (const f of this.floaters) {
      if (!f.active) continue;
      f.life -= dt;
      if (f.life <= 0) {
        f.active = false;
        continue;
      }
      f.y += f.vy * dt;
      f.vy += 12 * dt;
    }
  }

  draw(r: Renderer): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      const t = p.life / p.max;
      // fade by shrinking rather than alpha: keeps the pixel look crisp
      const size = p.kind === 1 && t < 0.4 ? 1 : p.size;
      r.rectWorld(p.x, p.y, size, size, p.color);
    }
    for (const f of this.floaters) {
      if (!f.active) continue;
      r.textWorld(f.text, f.x, f.y, f.color, 'center');
    }
  }
}
